import type { LangkahMentah } from "@/types/pengobatan"
import type { SeksiMentah } from "@/types/konten"

// Rekomendasi obat (diisi via admin: nama, gambar, harga, link Shopee)
export interface ObatItem {
  nama: string
  image?: string
  harga?: number
  shopeeLink?: string
}

export interface Disease {
  id: string
  name: string
  category: string
  severity: "Ringan" | "Sedang" | "Berat"
  description: string
  image: string
  images: string[]
  videoUrl?: string
  /**
   * Seksi isi. Boleh `string[]` (bentuk lama) atau daftar blok konten — sub-
   * judul, tabel, kotak sorotan (bentuk baru) — lihat @/types/konten.
   * Selalu lewatkan ke `blokTerisi()` / `teksDatar()` sebelum dipakai.
   */
  gejala: SeksiMentah
  penyebab: SeksiMentah
  /**
   * Langkah pengobatan. Boleh string (bentuk lama) atau objek berisi produk
   * & dosis (bentuk baru) — lihat @/types/pengobatan.
   */
  pengobatan: LangkahMentah[]
  pencegahan: SeksiMentah
  rentanPada: string[]
  obat?: ObatItem[] // opsional — dari admin/JSON sync
}

export const diseases: Disease[] = [
  {
    id: "newcastle-disease",
    name: "Newcastle Disease (ND/Tetelo)",
    category: "Virus",
    severity: "Berat",
    description:
      "Newcastle Disease (ND) atau yang dikenal dengan nama Tetelo adalah penyakit virus yang sangat menular dan mematikan pada ayam. Penyakit ini dapat menyebabkan kematian hingga 100% pada ayam yang tidak divaksinasi. ND disebabkan oleh virus Paramyxovirus dan dapat menyerang sistem pernapasan, pencernaan, dan saraf.",
    image: "/chicks.jpg",
    images: ["/chicks.jpg", "/feed.jpg"],
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    gejala: [
      "Ayam terlihat lesu dan nafsu makan menurun drastis",
      "Batuk, bersin, dan keluar cairan dari hidung",
      "Diare berwarna kehijauan",
      "Kepala dan leher terpuntir (torticolis)",
      "Sayap dan kaki lumpuh atau kejang",
      "Produksi telur menurun drastis atau berhenti",
      "Kematian mendadak tanpa gejala yang jelas",
    ],
    penyebab: [
      "Infeksi virus Paramyxovirus yang sangat menular",
      "Kontak langsung dengan ayam yang terinfeksi",
      "Kontaminasi melalui pakan, air minum, atau peralatan kandang",
      "Burung liar yang membawa virus",
      "Sanitasi kandang yang buruk",
      "Vaksinasi yang tidak tepat atau tidak dilakukan",
    ],
    pengobatan: [
      "TIDAK ADA obat yang dapat menyembuhkan ND",
      "Pisahkan segera ayam yang sakit dari kawanan sehat",
      "Berikan vitamin dan antibiotik untuk mencegah infeksi sekunder",
      "Tingkatkan kualitas pakan dan air minum",
      "Laporkan ke Dinas Peternakan setempat untuk penanganan lebih lanjut",
      "Ayam yang mati harus dibakar atau dikubur dalam-dalam",
    ],
    pencegahan: [
      "Vaksinasi ND secara rutin dan tepat jadwal (umur 4 hari, 21 hari, dan booster berkala)",
      "Jaga biosekuriti kandang dengan ketat",
      "Desinfeksi kandang, peralatan, dan kendaraan secara berkala",
      "Batasi akses orang luar dan hewan lain ke area kandang",
      "Karantina ayam baru minimal 2 minggu sebelum digabung",
      "Buang ayam mati dengan benar (bakar/kubur)",
      "Jaga kebersihan lingkungan kandang",
    ],
    rentanPada: ["Semua jenis ayam", "Terutama ayam yang tidak divaksinasi", "Ayam muda (DOC - grower)"],
  },
  {
    id: "coccidiosis",
    name: "Koksidiosis (Berak Darah)",
    category: "Parasit",
    severity: "Sedang",
    description:
      "Koksidiosis adalah penyakit parasit yang disebabkan oleh protozoa Eimeria yang menyerang saluran pencernaan ayam. Penyakit ini sangat umum terjadi dan dapat menyebabkan kerugian ekonomi yang besar karena penurunan pertumbuhan, produksi telur, dan kematian terutama pada ayam muda.",
    image: "/chicks.jpg",
    images: ["/chicks.jpg", "/assorted-eggs.png"],
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    gejala: [
      "Diare berdarah atau feses bercampur darah",
      "Bulu kusam dan mengembang",
      "Ayam terlihat lesu dan malas bergerak",
      "Nafsu makan menurun drastis",
      "Dehidrasi dan berat badan turun",
      "Ayam berkumpul di dekat sumber panas",
      "Pertumbuhan terhambat pada ayam muda",
    ],
    penyebab: [
      "Infeksi protozoa Eimeria (ada 9 spesies yang menginfeksi ayam)",
      "Kandang lembab dan sanitasi buruk",
      "Kepadatan kandang terlalu tinggi",
      "Litter basah dan kotor",
      "Stres akibat perubahan pakan atau lingkungan",
      "Sistem kekebalan tubuh ayam yang lemah",
    ],
    pengobatan: [
      "Berikan obat anti-koksidiosis (Amprolium, Sulfaquinoxaline, atau Toltrazuril)",
      "Pisahkan ayam yang sakit",
      "Ganti litter yang basah dan kotor",
      "Berikan vitamin K untuk membantu pembekuan darah",
      "Tingkatkan ventilasi kandang",
      "Pastikan ketersediaan air minum bersih",
      "Konsultasi dengan dokter hewan untuk dosis yang tepat",
    ],
    pencegahan: [
      "Jaga kebersihan dan kekeringan litter kandang",
      "Atur kepadatan kandang yang ideal",
      "Berikan pakan dengan koksdiostat (obat pencegah)",
      "Pastikan ventilasi kandang baik",
      "Hindari litter basah dengan sistem drainase yang baik",
      "Lakukan vaksinasi koksidiosis pada DOC",
      "Rotasi penggunaan obat anti-koksidiosis untuk mencegah resistensi",
    ],
    rentanPada: ["Ayam muda umur 3-6 minggu", "Ayam broiler", "Ayam petelur periode starter"],
  },
  {
    id: "infectious-coryza",
    name: "Snot/Coryza (Pilek Ayam)",
    category: "Bakteri",
    severity: "Sedang",
    description:
      "Infectious Coryza atau yang dikenal sebagai Snot/Pilek adalah penyakit pernapasan bakteri yang sangat menular pada ayam. Penyakit ini disebabkan oleh bakteri Avibacterium paragallinarum dan sering muncul saat musim hujan atau perubahan cuaca. Meskipun jarang mematikan, penyakit ini dapat menurunkan produktivitas secara signifikan.",
    image: "/chicks.jpg",
    images: ["/chicks.jpg"],
    gejala: [
      "Keluar cairan kental dari hidung dan mata",
      "Wajah bengkak, terutama di sekitar mata",
      "Mata berair dan merah",
      "Bersin-bersin dan sulit bernapas",
      "Nafsu makan menurun",
      "Produksi telur menurun 10-40%",
      "Bau tidak sedap di kandang",
    ],
    penyebab: [
      "Infeksi bakteri Avibacterium paragallinarum",
      "Ventilasi kandang buruk dan kelembaban tinggi",
      "Perubahan cuaca mendadak (terutama musim hujan)",
      "Kepadatan kandang terlalu tinggi",
      "Stres akibat transportasi atau handling",
      "Ayam carrier (pembawa) yang tidak menunjukkan gejala",
    ],
    pengobatan: [
      "Berikan antibiotik (Streptomycin, Erythromycin, atau Sulfadimethoxine)",
      "Isolasi ayam yang sakit dari kawanan sehat",
      "Tingkatkan ventilasi dan kurangi kelembaban kandang",
      "Berikan vitamin dan suplemen untuk meningkatkan imunitas",
      "Bersihkan cairan di hidung dan mata dengan air hangat",
      "Konsultasi dokter hewan untuk terapi yang tepat",
    ],
    pencegahan: [
      "Vaksinasi Coryza pada pullet (ayam muda betina)",
      "Jaga ventilasi kandang tetap optimal",
      "Hindari perubahan suhu dan kelembaban yang drastis",
      "Kurangi kepadatan kandang",
      "Karantina ayam baru sebelum digabung dengan kawanan",
      "Depopulasi dan sanitasi total jika wabah parah",
      "Berikan vitamin C saat musim hujan",
    ],
    rentanPada: ["Ayam petelur", "Ayam dewasa", "Semua umur (terutama saat musim hujan)"],
  },
  {
    id: "pullorum",
    name: "Pullorum Disease (Berak Putih)",
    category: "Bakteri",
    severity: "Berat",
    description:
      "Pullorum Disease atau Berak Putih adalah penyakit bakteri yang sangat berbahaya terutama pada anak ayam (DOC). Disebabkan oleh bakteri Salmonella pullorum, penyakit ini dapat ditularkan dari induk ke anak melalui telur (transovarial). Angka kematian pada DOC bisa mencapai 100%.",
    image: "/chicks.jpg",
    images: ["/chicks.jpg", "/feed.jpg"],
    gejala: [
      "DOC terlihat lemah dan menggigil",
      "Berkumpul di bawah sumber panas",
      "Diare putih kental dan lengket (white diarrhea)",
      "Kloaka kotor dan tersumbat oleh feses putih",
      "Mata tertutup dan sayap terkulai",
      "Nafsu makan hilang",
      "Kematian tinggi pada umur 2-3 minggu",
    ],
    penyebab: [
      "Infeksi bakteri Salmonella pullorum",
      "Penularan vertikal dari induk ke anak melalui telur",
      "Kontaminasi mesin tetas yang kotor",
      "Pakan dan air minum yang terkontaminasi",
      "Sanitasi kandang buruk",
      "DOC dari breeding farm yang tidak bersertifikat",
    ],
    pengobatan: [
      "Berikan antibiotik (Furazolidone, Sulfamerazine, atau Gentamicin)",
      "Isolasi DOC yang sakit",
      "Tingkatkan suhu kandang 1-2°C",
      "Berikan elektrolit dan vitamin",
      "Bersihkan kloaka dari kotoran yang menempel",
      "Konsultasi dokter hewan untuk penanganan intensif",
      "Ayam yang terinfeksi sebaiknya dimusnahkan untuk mencegah carrier",
    ],
    pencegahan: [
      "Beli DOC HANYA dari breeding farm bersertifikat dan bebas Pullorum",
      "Lakukan tes Pullorum pada induk secara berkala",
      "Jaga kebersihan mesin tetas dan hatchery",
      "Desinfeksi telur tetas sebelum dimasukkan mesin",
      "Berikan probiotik pada DOC sejak hari pertama",
      "Sanitasi kandang brooding dengan ketat",
      "Jaga suhu brooding optimal (32-35°C minggu pertama)",
    ],
    rentanPada: ["DOC (Day Old Chick)", "Anak ayam umur 1-3 minggu", "Semua jenis ayam"],
  },
  {
    id: "gumboro",
    name: "Gumboro Disease (IBD)",
    category: "Virus",
    severity: "Berat",
    description:
      "Infectious Bursal Disease (IBD) atau Gumboro adalah penyakit virus yang sangat menular dan menyerang sistem kekebalan tubuh ayam. Virus ini menyerang organ bursa fabricius yang berperan penting dalam pembentukan antibodi. Ayam yang terinfeksi akan rentan terhadap penyakit lain.",
    image: "/chicks.jpg",
    images: ["/chicks.jpg", "/assorted-eggs.png"],
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    gejala: [
      "Ayam terlihat depresi dan lesu mendadak",
      "Bulu mengembang dan kepala terbenam ke dalam badan",
      "Diare putih berair",
      "Dehidrasi parah",
      "Kematian mendadak pada puncak infeksi (hari ke-3 hingga ke-4)",
      "Angka kematian 20-30% dalam 5-7 hari",
      "Ayam yang sembuh akan terhambat pertumbuhannya",
    ],
    penyebab: [
      "Infeksi virus IBD (Infectious Bursal Disease virus)",
      "Virus sangat tahan di lingkungan (bisa bertahan berbulan-bulan)",
      "Penularan melalui feses, pakan, air, dan peralatan",
      "Litter yang terkontaminasi",
      "Vaksinasi yang tidak tepat waktu atau dosis tidak sesuai",
      "Stres dan kondisi kandang buruk",
    ],
    pengobatan: [
      "TIDAK ADA obat spesifik untuk Gumboro",
      "Berikan vitamin dan elektrolit untuk mengurangi dehidrasi",
      "Berikan antibiotik untuk mencegah infeksi sekunder",
      "Tingkatkan kualitas pakan",
      "Jaga suhu dan kelembaban kandang stabil",
      "Isolasi ayam sakit",
      "Fokus pada perawatan suportif",
    ],
    pencegahan: [
      "Vaksinasi Gumboro yang tepat waktu (umur 10-14 hari)",
      "Gunakan vaksin dengan strain yang sesuai dengan kondisi lapangan",
      "Pastikan maternal antibodi sudah turun saat vaksinasi",
      "Jaga biosekuriti kandang dengan ketat",
      "Desinfeksi kandang dengan disinfektan yang efektif untuk IBD",
      "Atur waktu kosong kandang (rest period) minimal 2 minggu",
      "Jangan mencampur ayam dari umur berbeda dalam satu kandang",
    ],
    rentanPada: ["Ayam muda umur 3-6 minggu", "Ayam broiler", "Semua jenis ayam"],
  },
]
